import { useState } from 'react';
import { ImageType, PdfFileType } from 'types';
import { useAppContext } from 'context/AppContext';
import { useStyleContext } from 'context/StyleContext';
import { TEXT_AREA_IDS } from 'components/AiMode/constants';
import { handleThemeBuilderAiModeRequest } from 'services/api/theme-builder-ai-mode-fetch';

export const useChatInput = () => {
  const { resetStyleState, styleState } = useStyleContext();
  const {
    isStreaming,
    setIsStreaming,
    chatMessages,
    updateLastAiMessage,
    abortControllerRef,
    activeView,
  } = useAppContext();

  const [userInput, setUserInput] = useState<string>('');

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 210) + 'px';
    if (e.target.id === TEXT_AREA_IDS.CHAT_INPUT) {
      document.documentElement.style.setProperty('--user-input-height', el.style.height);
    }
  };

  const handleCancelStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const changeToStreamingAndStartStreaming = (
    userPdfAndImages?: { pdfs: PdfFileType[]; images: ImageType[] } | undefined
  ) => {
    setIsStreaming(true);
    handleThemeBuilderAiModeRequest({
      userInput: {
        text: userInput,
        images: userPdfAndImages?.images,
        pdfs: userPdfAndImages?.pdfs,
      },
      chatMessages,
      isStreaming,
      setIsStreaming,
      abortControllerRef,
      resetStyleState,
      updateLastAiMessage,
      styleState,
      activeView,
    });
    setUserInput('');
  };

  const handleEnterKeyPress = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    userPdfAndImages?: { pdfs: PdfFileType[]; images: ImageType[] } | undefined
  ) => {
    if (!userInput.trim()) {
      return;
    }
    if (isStreaming) {
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const el = e.currentTarget;
      el.style.height = 'auto';
      if (el.id === TEXT_AREA_IDS.CHAT_INPUT) {
        document.documentElement.style.setProperty('--user-input-height', '84px');
      }
      changeToStreamingAndStartStreaming(userPdfAndImages);
    }
  };

  const handleArrowUpClick = (
    userPdfAndImages?: { pdfs: PdfFileType[]; images: ImageType[] } | undefined
  ) => {
    if (isStreaming) {
      handleCancelStreaming();
      return;
    }
    if (!userInput.trim()) {
      return;
    }
    changeToStreamingAndStartStreaming(userPdfAndImages);
  };

  return {
    handleTextareaInput,
    handleEnterKeyPress,
    handleArrowUpClick,
    userInput,
    updateUserInput: setUserInput,
  };
};
